import { useLingui } from '@lingui/react/macro'
import { CantileverButton } from 'components/CantileverButton'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`

const Dialog = styled.div`
  min-width: 22rem;
  max-width: 32rem;
  padding: 1.25rem 1.25rem 1rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  color: ${({ theme }) => theme.forecast.defaultText};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.4);
`

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
`

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.8rem;
`

const NameInput = styled.input`
  height: 2.25rem;
  padding: 0 0.625rem;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.25rem;
`

interface NamePromptModalProps {
  title: string
  subtitle?: string
  initialValue?: string
  confirmLabel?: string
  onConfirm: (name: string) => void
  onCancel: () => void
}

export const NamePromptModal: React.FC<NamePromptModalProps> = ({
  title,
  subtitle,
  initialValue = '',
  confirmLabel,
  onConfirm,
  onCancel,
}) => {
  const { t } = useLingui()
  const [value, setValue] = useState(initialValue)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed) return
    onConfirm(trimmed)
  }

  return (
    <Backdrop onMouseDown={onCancel}>
      <Dialog onMouseDown={(event) => event.stopPropagation()}>
        <Title>{title}</Title>
        {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
        <NameInput
          ref={inputRef}
          value={value}
          placeholder={t`Enter a name`}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit()
            if (event.key === 'Escape') onCancel()
          }}
        />
        <Actions>
          <CantileverButton ghost onClick={onCancel}>
            {t`Cancel`}
          </CantileverButton>
          <CantileverButton primary onClick={submit} disabled={!value.trim()}>
            {confirmLabel ?? t`Save`}
          </CantileverButton>
        </Actions>
      </Dialog>
    </Backdrop>
  )
}
